import React from 'react';
import { Alert, StyleSheet, Text, View } from './components/base_components';
import { BusyComponent } from './components/busy_component';
import ModalContent from './components/modal_content';
import ProgressSpinner from './components/progress_spinner';
import TextButton from './components/buttons/text_button';
import TxOverlay from './components/overlays/tx_overlay';

import OrderHelper from './lib/order_helper';
import OrderHistoryStore from './stores/order_history_store';
import WalletStore from './stores/wallet_store';

import Analytics from './tools/analytics';
import { isSameAddress } from './tools/eth_tools';
import { numberToDisplay } from './tools/util';

const styles = StyleSheet.create({
  orderScreen: {
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  spinner: {
    marginTop: '2rem',
  },
  spacer: {
    flex: 1,
  },
  topBubbleText: {
    marginTop: '2rem',
    width: '32rem',
    marginLeft: '1rem',
    color: 'black',
    fontSize: '2rem',
  },
  market: {
    alignSelf: 'center',
    marginTop: '0.5rem',
    width: '32rem',
    paddingTop: '1rem',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#ddd',
    borderRadius: '0.5rem',
    backgroundColor: '#fff',
    flexDirection: 'column',
  },
  bubbleText: {
    marginTop: '1rem',
    marginLeft: '1rem',
    color: 'black',
    fontSize: '2rem',
  },
  marketTitle: {
    marginLeft: '1.5rem',
    color: '#333',
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  orderList: {
    marginTop: '1.5rem',
    flexDirection: 'column',
  },
  order: {
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    paddingTop: '1rem',
    paddingBottom: '1rem',
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  oddOrder: {
    backgroundColor: '#eee',
  },
  top: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  left: {
    flexDirection: 'column',
  },
  type: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  priceQuantity: {
    marginTop: '0.5rem',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  quantity: {
    color: '#555',
    fontSize: '1.3rem',
    fontWeight: '500',
  },
  at: {
    marginLeft: '0.5rem',
    marginRight: '0.5rem',
    color: '#555',
    fontSize: '1.3rem',
  },
  price: {
    color: '#555',
    fontSize: '1.3rem',
    fontWeight: '500',
  },
  buttons: {
    flexDirection: 'column',
  },
  button: {
    height: '3rem',
  },
  settleButton: {
    marginBottom: '1rem',
    height: '3rem',
  },
  oldOrderList: {
    marginTop: '1.5rem',
    alignSelf: 'center',
    width: '32rem',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#ddd',
    borderRadius: '0.5rem',
    backgroundColor: '#fff',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
});

type Props = {};
type State = {
  has_wallet: boolean;
  is_loaded: boolean;
  market_list: any;
  history_list: any;
};
export default class OrderScreen extends BusyComponent<Props, State> {
  state = {
    has_wallet: WalletStore.hasWallet(),
    is_loaded: false,
    market_list: null,
    history_list: null,
  };
  _disarm = false;
  componentDidMount() {
    Analytics.showScreen('order');
    WalletStore.addChangeListener(this._onWalletUpdate);
    OrderHistoryStore.addChangeListener(this._onOrderUpdate);
    this._onWalletUpdate();
    this._onOrderUpdate();
  }
  componentWillUnmount() {
    WalletStore.removeChangeListener(this._onWalletUpdate);
    OrderHistoryStore.removeChangeListener(this._onOrderUpdate);
  }
  _onWalletUpdate = () => {
    const has_wallet = WalletStore.hasWallet();
    if (has_wallet) {
      OrderHistoryStore.fetch();
    }
    this.setState({ has_wallet });
  };
  _onOrderUpdate = () => {
    const is_loaded = OrderHistoryStore.isLoaded();
    const order_list = OrderHistoryStore.getList();
    const market_list = _makeMarkets(order_list);
    const history_list = _makeHistory(order_list);
    this.setState({ is_loaded, market_list, history_list });
  };
  _onCancelPress = (order) => {
    Alert.alert('Order Cancel', 'Are you sure you want to cancel this order?', [
      {
        text: 'Yes',
        onPress: () => this._onCancel(order),
      },
      {
        text: 'No',
        onPress: noop,
      },
    ]);
  };
  _onCancel = (order) => {
    if (this.setBusy()) {
      OrderHelper.cancelOrder(order, (err, data) => {
        this.clearBusy();
        if (err) {
          Alert.alert('Order Cancel', 'Cancel failed');
        } else {
          const overlay = (
            <TxOverlay
              title='Order Cancel'
              data={data}
              onDone={this._onTxDone}
              onSuccess={this._onTxDone}
              onError={this._onTxDone}
            />
          );
          ModalContent.showOverlay(overlay);
        }
      });
    }
  };
  _onSettlePress = (order) => {
    if (this.setBusy()) {
      OrderHelper.settleOrder(order, (err, data) => {
        this.clearBusy();
        if (err) {
          Alert.alert('Order Cancel', 'Cancel failed');
        } else {
          const overlay = (
            <TxOverlay
              title='Settle Order'
              data={data}
              onDone={this._onTxDone}
              onSuccess={this._onTxDone}
              onError={this._onTxDone}
            />
          );
          ModalContent.showOverlay(overlay);
        }
      });
    }
  };
  _onTxDone = () => {
    ModalContent.hideOverlay();
  };

  render() {
    const { has_wallet, is_loaded, market_list, history_list } = this.state;

    let content = null;
    if (has_wallet && is_loaded) {
      let markets = null;
      let history = null;
      if (market_list && market_list.length > 0) {
        markets = market_list.map((market, i) => (
          <Market
            key={i}
            market={market}
            onCancelPress={this._onCancelPress}
            onSettlePress={this._onSettlePress}
          />
        ));
      } else {
        markets = <Text style={styles.noOrdersText}>No active orders.</Text>;
      }
      if (history_list && history_list.length > 0) {
        history = (
          <View style={styles.oldOrderList}>
            {history_list.map((order, i) => (
              <OldOrder odd={i % 2 === 1} order={order} />
            ))}
          </View>
        );
      } else {
        history = (
          <Text style={styles.noOrdersText}>No historical orders found</Text>
        );
      }

      content = (
        <>
          <Text style={styles.topBubbleText}>Active Orders</Text>
          {markets}
          <Text style={styles.topBubbleText}>Order History</Text>
          {history}
        </>
      );
    } else if (has_wallet && is_loaded) {
      content = (
        <>
          <Text style={styles.topBubbleText}>No active orders!</Text>
        </>
      );
    } else if (has_wallet) {
      content = (
        <>
          <ProgressSpinner color='black' />;
        </>
      );
    } else {
      content = (
        <>
          <Text style={styles.topBubbleText}>Connect to see your orders</Text>
        </>
      );
    }
    return <View style={styles.orderScreen}>{content}</View>;
  }
}

function Market(props) {
  const { token, currency, order_list } = props.market;
  const orders = order_list.map((order, i) => (
    <Order
      key={i}
      odd={i % 2 === 0}
      order={order}
      onCancelPress={props.onCancelPress}
      onSettlePress={props.onSettlePress}
    />
  ));

  const name = `${token.symbol} <> ${currency.symbol}`;
  return (
    <View style={styles.market}>
      <Text style={styles.marketTitle}>{name}</Text>
      <View style={styles.orderList}>{orders}</View>
    </View>
  );
}
function Order(props) {
  const {
    is_sell,
    currency,
    token,
    price,
    order_quantity,
    settle_direction,
  } = props.order;
  function _onCancelPress() {
    props.onCancelPress(props.order);
  }
  function _onSettlePress() {
    props.onSettlePress(props.order);
  }
  let settle = null;
  if (settle_direction) {
    settle = (
      <TextButton
        style={styles.settleButton}
        type='default'
        text='Settle'
        onPress={_onSettlePress}
      />
    );
  }
  return (
    <View style={[styles.order, props.odd ? styles.oddOrder : null]}>
      <View style={styles.left}>
        <Text style={styles.type}>{is_sell ? 'Sell' : 'Buy'}</Text>
        <View style={styles.priceQuantity}>
          <Text style={styles.quantity}>
            {numberToDisplay(order_quantity) + ' ' + token.symbol}
          </Text>
          <Text style={styles.at}>@</Text>
          <Text style={styles.price}>
            {numberToDisplay(price) + ' ' + currency.symbol}
          </Text>
        </View>
      </View>
      <View style={styles.spacer} />
      <View style={styles.buttons}>
        {settle}
        <TextButton
          style={styles.button}
          type='ghost-inverted'
          text='Cancel'
          onPress={_onCancelPress}
        />
      </View>
    </View>
  );
}
function OldOrder(props) {
  const {
    is_sell,
    currency,
    token,
    price,
    order_quantity,
    created_time,
  } = props.order;
  const created = new Date(created_time).toLocaleDateString();
  return (
    <View style={[styles.order, props.odd ? styles.oddOrder : null]}>
      <View style={styles.left}>
        <Text style={styles.type}>{is_sell ? 'Sell' : 'Buy'}</Text>
        <View style={styles.priceQuantity}>
          <Text style={styles.quantity}>
            {numberToDisplay(order_quantity) + ' ' + token.symbol}
          </Text>
          <Text style={styles.at}>@</Text>
          <Text style={styles.price}>
            {numberToDisplay(price) + ' ' + currency.symbol}
          </Text>
        </View>
      </View>
      <View style={styles.spacer} />
      <View style={styles.right}>
        <Text style={styles.created}>{created}</Text>
      </View>
    </View>
  );
}

function _makeMarkets(list) {
  let market_list = [];
  list.forEach((order) => {
    const { chain_id, token, currency, is_cancel, is_done } = order;
    if (!is_done && !is_cancel) {
      let market = market_list.find(
        (m) =>
          m.chain_id === chain_id &&
          isSameAddress(m.token.contract_addr, token.contract_addr) &&
          isSameAddress(m.currency.contract_addr, currency.contract_addr)
      );
      if (!market) {
        market = {
          chain_id,
          token,
          currency,
          order_list: [],
        };
        market_list.push(market);
      }
      market.order_list.push(order);
    }
  });
  return market_list;
}
function _makeHistory(list) {
  let history_list = list.filter((order) => order.is_done && !order.is_cancel);
  history_list.sort(_historySort);
  return history_list;
}
function _historySort(a, b) {
  const a_ts = Date.parse(a.created_time);
  const b_ts = Date.parse(b.created_time);
  return b_ts - a_ts;
}
function noop() {}
